import logger from '../../../utils/logger';
import { Material } from '../../../../types/api/Material';
import { MixDesignItem } from '../../../../types/batch/MixDesignItem';
import convertUnit from '../../../utils/unit-converter';
import { toNumber } from '../../../utils/numbers';
import { getRemainingSchedule } from './proportion-schedule';

export const updateMixDesignForSubstitution = (
  runningData: any,
  originalMaterial: Material,
  substituteMaterial: Material,
  scaleData: any,
  currentWeight: number,
  scaleUnit?: string,
): void => {
  if (!runningData.mixDesign) {
    return;
  }

  const originalTarget = scaleData.mft;
  // Includes proportion two when the original is swapped during proportion one; the substitute feeds it later.
  const remainingWeight = getRemainingSchedule(scaleData, currentWeight).total;

  logger.info('[MIX_DESIGN_UPDATE] Starting mixDesign update for material swap', {
    originalMaterial: originalMaterial.code,
    substituteMaterial: substituteMaterial.code,
    originalTarget,
    actualDispensed: currentWeight,
    remainingToDispense: remainingWeight,
  });

  updateOriginalMaterialWeight(
    runningData.mixDesign,
    originalMaterial,
    substituteMaterial,
    currentWeight,
  );

  handleSubstituteMaterial(
    runningData.mixDesign,
    originalMaterial,
    substituteMaterial,
    scaleData,
    remainingWeight,
    scaleUnit,
  );

  logFinalMixDesignState(runningData.mixDesign);
};

const updateOriginalMaterialWeight = (
  mixDesign: MixDesignItem[],
  originalMaterial: Material,
  substituteMaterial: Material,
  currentWeight: number,
): void => {
  let originalMaterialUpdated = false;

  mixDesign.forEach((item: MixDesignItem, index: number) => {
    if (isSameMaterial(item, originalMaterial)) {
      const previousActualWeight = mixDesign[index].actualWeight || 0;
      // The same material can be interrupted more than once (A -> B -> A -> C), so add to what it already delivered.
      mixDesign[index].actualWeight = previousActualWeight + currentWeight;
      mixDesign[index].wasSubstituted = true;
      mixDesign[index].substituteMaterialId = substituteMaterial.itemId;
      mixDesign[index].substituteMaterialCode = substituteMaterial.code;

      originalMaterialUpdated = true;

      logger.info('[MIX_DESIGN_UPDATE] Updated original material actual weight', {
        material: originalMaterial.code,
        previousActualWeight,
        newActualWeight: mixDesign[index].actualWeight,
        target: item.quantityFinal || item.quantity,
      });
    }
  });

  if (!originalMaterialUpdated) {
    logger.warn('[MIX_DESIGN_UPDATE] Original material not found in mixDesign', {
      originalMaterial: originalMaterial.code,
    });
  }
};

const handleSubstituteMaterial = (
  mixDesign: MixDesignItem[],
  originalMaterial: Material,
  substituteMaterial: Material,
  scaleData: any,
  remainingWeight: number,
  scaleUnit?: string,
): void => {
  const substituteIndex = mixDesign.findIndex((item: MixDesignItem) =>
    isSameMaterial(item, substituteMaterial),
  );

  if (substituteIndex !== -1) {
    updateExistingSubstituteMaterial(
      mixDesign,
      substituteIndex,
      substituteMaterial,
      remainingWeight,
      scaleUnit,
    );
  } else {
    addNewSubstituteMaterial(
      mixDesign,
      originalMaterial,
      substituteMaterial,
      scaleData,
      remainingWeight,
      scaleUnit,
    );
  }
};

const updateExistingSubstituteMaterial = (
  mixDesign: MixDesignItem[],
  substituteIndex: number,
  substituteMaterial: Material,
  remainingWeight: number,
  scaleUnit?: string,
): void => {
  const substitute = mixDesign[substituteIndex];
  substitute.includesSubstitution = true;
  // The design target stays as-is for the ticket. Track the extra quantity this constituent now has to cover
  // separately, so target recalculations (e.g. jog only on last drop) still include it.
  const transferred = scaleUnit && substitute.unitSymbol
    ? toNumber(convertUnit(Math.max(0, remainingWeight), scaleUnit, substitute.unitSymbol), 5)
    : Math.max(0, remainingWeight);
  substitute.substitutedQuantity = toNumber((substitute.substitutedQuantity || 0) + transferred, 5);

  logger.info('[MIX_DESIGN_UPDATE] Updated existing substitute material weight', {
    material: substituteMaterial.code,
    target: substitute.quantityBatchSize || substitute.quantityFinal || substitute.quantity,
    remainingWeight,
  });
};

const addNewSubstituteMaterial = (
  mixDesign: MixDesignItem[],
  originalMaterial: Material,
  substituteMaterial: Material,
  scaleData: any,
  remainingWeight: number,
  scaleUnit?: string,
): void => {
  const unitSymbol = substituteMaterial.itemBatchUnitSymbol ||
    scaleData.constituent?.unitSymbol ||
    originalMaterial.itemBatchUnitSymbol;
  const sourceUnit = scaleUnit || scaleData.constituent?.unitSymbol || originalMaterial.itemBatchUnitSymbol || unitSymbol;
  const newEntry: MixDesignItem = {
    constituentItemId: substituteMaterial.itemId,
    constituentItemCode: substituteMaterial.code,
    batchCode: substituteMaterial.batchCode,
    actualWeight: 0,
    quantity: 0,
    quantityFinal: 0,
    quantityBatchSize: sourceUnit && unitSymbol
      ? toNumber(convertUnit(remainingWeight, sourceUnit, unitSymbol), 5)
      : remainingWeight,
    unitSymbol,
    isSubstitute: true,
    originalMaterialId: originalMaterial.itemId,
    originalMaterialCode: originalMaterial.code,
  };

  const originalIndex = mixDesign.findIndex(item => isSameMaterial(item, originalMaterial));
  mixDesign.splice(originalIndex === -1 ? mixDesign.length : originalIndex + 1, 0, newEntry);

  logger.info('[MIX_DESIGN_UPDATE] Added new substitute material to mixDesign', {
    material: substituteMaterial.code,
    weight: remainingWeight,
    originalMaterial: originalMaterial.code,
  });
};

const isSameMaterial = (item: MixDesignItem, material: Material): boolean => {
  return item.constituentItemId === material.itemId ||
    (!!item.batchCode && !!material.batchCode && item.batchCode === material.batchCode);
};

const logFinalMixDesignState = (mixDesign: MixDesignItem[]): void => {
  logger.info('[MIX_DESIGN_UPDATE] Final mixDesign state after substitution', {
    materials: mixDesign.map((item: MixDesignItem) => ({
      code: item.constituentItemCode,
      actualWeight: item.actualWeight || 0,
      target: item.quantityFinal || item.quantity,
      wasSubstituted: item.wasSubstituted || false,
      isSubstitute: item.isSubstitute || false,
    })),
  });
};
